import { logger } from "@/modules/logger";
import { api } from "@/modules/api";
import { AudioRecordingRepository } from "@/domains/dexie-db/repositories/audio-recording/audio-recording";
import { AudioRecordingUploadStatus } from "@/store/audio-recordings";
import { Uuid } from "@/domains/global/identifiers";
import {
  AudioRecordingTransformProgressTracker,
  AudioRecordingTransformingState,
} from "@/store/audio/transforming/audio-transforming.types";
import { uuidModule } from "@/modules/uuid";
import { TranscriptionStatus } from "@/store/audio/recording";
import {
  enqueueRecordingFailureModal,
  RecordingFailureReason,
} from "@/store/audio/recording-failure-modal";
import type { ModalsStore } from "@/store/modals/ModalsStore";
import type { AppStoreNavigationStore } from "@/store/navigation/AppStoreNavigationStore";

type TransformJob = {
  audioRecordingId: Uuid;
};

type Dependencies = {
  audioRecordings: AudioRecordingRepository;
  progressTracker: AudioRecordingTransformProgressTracker;
  getTranscriptionStatus?: (audioRecordingId: Uuid) => TranscriptionStatus;
  clearTranscriptionStatus?: (audioRecordingId: Uuid) => void;
  modals: ModalsStore;
  navigation: AppStoreNavigationStore;
};

export class AudioRecordingTransformer {
  private readonly deps: Dependencies;
  private readonly queuedJobs: TransformJob[] = [];
  private readonly queuedJobKeys = new Set<Uuid>();
  private isRunning = false;

  constructor(deps: Dependencies) {
    this.deps = deps;
  }

  enqueue(job: TransformJob): void {
    const key = this.buildJobKey(job.audioRecordingId);
    if (this.queuedJobKeys.has(key)) return;

    this.queuedJobKeys.add(key);
    this.queuedJobs.push(job);

    // Set transform state immediately so UI shows "Processing" instead of "Uploading"
    this.deps.progressTracker.setTransformState({
      audioRecordingId: job.audioRecordingId,
      state: {
        isTransforming: true,
        errorMessage: null,
        awaitingRemoteUpdate: false,
        apiCallCompletedAt: null,
      },
    });

    void this.runQueue();
  }

  get debugQueuedJobs(): TransformJob[] {
    return [...this.queuedJobs];
  }

  private async runQueue(): Promise<void> {
    if (this.isRunning) {
      return;
    }
    this.isRunning = true;
    try {
      while (this.queuedJobs.length) {
        const job = this.queuedJobs[0];
        const key = this.buildJobKey(job.audioRecordingId);
        try {
          await this.processTransformJob(job);
        } catch (error) {
          logger.error({
            message: "[AudioRecordingTransformer] transform failure",
            info: { error: error instanceof Error ? error.message : String(error) },
          });
        } finally {
          this.queuedJobs.shift();
          this.queuedJobKeys.delete(key);
        }
      }
    } finally {
      this.isRunning = false;
    }
  }

  private async processTransformJob({ audioRecordingId }: TransformJob): Promise<void> {
    const { audioRecordings, progressTracker, modals, navigation } = this.deps;

    const updateState = (state: AudioRecordingTransformingState) =>
      progressTracker.setTransformState({ audioRecordingId, state });

    const showProcessingFailureModal = ({ associatedNoteId }: { associatedNoteId?: Uuid | null }) =>
      enqueueRecordingFailureModal({
        modals,
        navigation,
        reason: RecordingFailureReason.FailedToProcess,
        associatedNoteId,
      });

    const recording = await audioRecordings.getById({ id: audioRecordingId });
    if (!recording) {
      logger.warn({
        message: "[AudioRecordingTransformer] Missing audio recording",
        info: { audioRecordingId },
      });
      updateState({
        isTransforming: false,
        errorMessage: "Recording not found",
        awaitingRemoteUpdate: false,
        apiCallCompletedAt: null,
      });
      return;
    }

    // Only attempt transformation if upload completed successfully.
    if (recording.data.upload_status !== AudioRecordingUploadStatus.COMPLETED) {
      logger.warn({
        message: "[AudioRecordingTransformer] Skipping transform; upload incomplete",
        info: { audioRecordingId, uploadStatus: recording.data.upload_status },
      });
      progressTracker.clearTransformState({ audioRecordingId });
      return;
    }

    const noteId = recording.data.associated_note_id;
    if (!noteId) {
      logger.warn({
        message: "[AudioRecordingTransformer] Missing associated note id",
        info: { audioRecordingId },
      });
      await audioRecordings.markAsProcessingErrored({ id: audioRecordingId });
      updateState({
        isTransforming: false,
        errorMessage: "Recording is not linked to a note",
        awaitingRemoteUpdate: false,
        apiCallCompletedAt: null,
      });
      showProcessingFailureModal({ associatedNoteId: null });
      return;
    }
    await audioRecordings.markAsProcessingInProgress({ id: audioRecordingId });
    updateState({
      isTransforming: true,
      errorMessage: null,
      awaitingRemoteUpdate: false,
      apiCallCompletedAt: null,
    });

    try {
      await this.invokeTransformApi({
        noteId,
        audioRecordingId,
        forceTranscriptRegeneration:
          this.deps.getTranscriptionStatus?.(audioRecordingId) === TranscriptionStatus.Compromised,
      });

      updateState({
        isTransforming: false,
        errorMessage: null,
        awaitingRemoteUpdate: true,
        apiCallCompletedAt: Date.now(),
      });
      this.deps.clearTranscriptionStatus?.(audioRecordingId);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      await audioRecordings.markAsProcessingErrored({ id: audioRecordingId });

      updateState({
        isTransforming: false,
        errorMessage: message,
        awaitingRemoteUpdate: false,
        apiCallCompletedAt: null,
      });

      showProcessingFailureModal({ associatedNoteId: noteId });

      logger.error({
        message: "[AudioRecordingTransformer] Transform request failed",
        info: { audioRecordingId, error: message },
      });

      this.deps.clearTranscriptionStatus?.(audioRecordingId);
    }
  }

  private async invokeTransformApi({
    noteId,
    audioRecordingId,
    forceTranscriptRegeneration = false,
  }: {
    noteId: string;
    audioRecordingId: Uuid;
    forceTranscriptRegeneration?: boolean;
  }): Promise<void> {
    const associatedCapturedAudioId = uuidModule.resolveAudioRecordingCapturedAudioUuid({
      associatedAudioRecordingId: audioRecordingId,
    });

    const associatedSessionId = uuidModule.resolveCapturedAudioSessionSyncModelUuid({
      capturedAudioId: associatedCapturedAudioId,
    });

    const response = await api.post("/v2/voice/transform_note_using_audio_recording", {
      body: {
        note_id: noteId,
        audio_recording_id: audioRecordingId,
        associated_session_id: associatedSessionId,
        force_transcript_generation_using_audio_recording: forceTranscriptRegeneration,
      },
    });

    if (!response.response.ok) {
      const detail = response.error?.detail || "Failed to trigger transform";
      throw new Error(detail);
    }
  }

  private buildJobKey(audioRecordingId: Uuid): string {
    return audioRecordingId;
  }

  resetState() {
    this.queuedJobs.splice(0);
    this.queuedJobKeys.clear();
    this.isRunning = false;
  }
}
